import React, { useMemo } from "react";
import { useRequest } from "ahooks";
import { api } from "@redux/api";
import { useAuth } from "@/hooks/use-auth";
import { Nopermission } from "@/components/Nopermission";
import { useNavigate } from "react-router-dom";
import { emitEvent } from "@/hooks/useEventBus";
import { useDispatch, useSelector } from "react-redux";
import appActions from "@redux/app/actions";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import SwitchablePieChart from "./SwitchablePieChart";
import ColumnChart from "@containers/UtilityData/Charts/ColumnChart";

const STATUS_COLORS = {
  "Completed": "#4ade80",
  "Verifying": "#60a5fa",
  "On-Hold": "#9ca3af",
  "InProgress": "#fb923c",
  "Not Started": "#ef4444",
};

const PRIORITY_COLORS = {
  "Urgent": "#ef4444",
  "Important": "#fb923c",
  "Low": "#4ade80",
};

const WorkorderChart = () => {
  const workorderPermission = useAuth("workorder", 1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const facilityId = useSelector((state) => state?.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));

  const { data: workorderData } = useRequest(
    () => api.allworkorderview(),
    {
      ready: workorderPermission,
      cacheKey: `workorder-combined-chart-data-${facilityId}`,
      staleTime: 1 * 60 * 1000,
    }
  );

  if (!workorderPermission) {
    return <Nopermission />;
  }

  const groupedStatusData = workorderData?.data?.grouped_status_data;
  const groupedPriorityData = workorderData?.data?.grouped_priority_data;
  const past7Past30Data = workorderData?.data?.past7_past30;

  if (!groupedStatusData && !groupedPriorityData && !past7Past30Data) {
    return null;
  }

  const handleStatusClick = (e) => {
    emitEvent("CLOSE_WORKORDER");
    const url =
      buildFacilityPath("workorder", {
        ...facilityContext,
        query: { status: e.data.name },
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["workorder"]));
  };

  const handlePriorityClick = (e) => {
    emitEvent("CLOSE_WORKORDER");
    const url =
      buildFacilityPath("workorder", {
        ...facilityContext,
        query: { priority: e.data.name },
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["workorder"]));
  };

  const handleWorkSummaryClick = (e) => {
    emitEvent("CLOSE_WORKORDER");
    let params = "";
    switch (e.date_range) {
      case "Past 30 Days":
        params += "modify_date=" + 30;
        break;
      case "Past 7 Days":
        params += "modify_date=" + 7;
        break;
      case "Past 90 Days":
        params += "modify_date=" + 90;
        break;
      default:
        break;
    }
    params += "&status=" + e.status;

    const url =
      buildFacilityPath("workorder", {
        ...facilityContext,
        query: params,
      }) || "/switch-view";
    navigate(url);
    dispatch(appActions.changeCurrent(["workorder"]));
  };

  const configs = [
    groupedStatusData && {
      key: "status",
      title: "Workorder By Status",
      label: "Status",
      data: groupedStatusData,
      colors: STATUS_COLORS,
      onClick: handleStatusClick,
    },
    groupedPriorityData && {
      key: "priority",
      title: "Workorder By Priority",
      label: "Priority",
      data: groupedPriorityData,
      colors: PRIORITY_COLORS,
      onClick: handlePriorityClick,
    },
    past7Past30Data && {
      key: "summary",
      label: "Summary",
      render: () => (
        <ColumnChart
          data={past7Past30Data}
          title="Workorder Summary"
          slider={false}
          isStack={false}
          isGroup={true}
          handleChartClick={handleWorkSummaryClick}
        />
      ),
    },
  ].filter(Boolean);

  return (
    <SwitchablePieChart configs={configs} defaultActiveKey="status" />
  );
};

export default WorkorderChart;
